import { createAsyncThunk, createSlice, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { StoresEnum } from '@ubnt/link-core/enums';
import type { RootState } from 'app/store/reducer';
import { settingsCountrySelector } from 'modules/settings/slice';
import { LIST_PRICES_ENDPOINT, getStoreCountry } from 'modules/prices/utils';
import { REQUEST_STATUS } from 'constants/request';

const SLICE_NAME = 'prices';

export const fetchPrices = createAsyncThunk(
  `${SLICE_NAME}/fetch`,
  async (storeCountry: ValuesOf<typeof StoresEnum>) => {
    LIST_PRICES_ENDPOINT!.searchParams.set('store', storeCountry);
    const response = await fetch(LIST_PRICES_ENDPOINT!);
    return (await response.json()) as Record<string, PriceItem>;
  },
  {
    condition: (storeCountry, { getState }) => {
      const state = getState() as RootState;
      if (!LIST_PRICES_ENDPOINT || isPricesPendingOrFulfilledSelector(state, storeCountry)) {
        return false;
      }
      return true;
    },
  }
);

interface PricesState {
  id: ValuesOf<typeof StoresEnum>;
  requestStatus: ValuesOf<typeof REQUEST_STATUS>;
  prices?: Prices;
}

const pricesAdapter = createEntityAdapter<PricesState>();

const pricesSlice = createSlice({
  name: SLICE_NAME,
  initialState: pricesAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPrices.pending, (state, action) => {
      const id = action.meta.arg;
      pricesAdapter.upsertOne(state, { id, requestStatus: REQUEST_STATUS.PENDING });
    });
    builder.addCase(fetchPrices.rejected, (state, action) => {
      const id = action.meta.arg;
      pricesAdapter.upsertOne(state, { id, requestStatus: REQUEST_STATUS.REJECTED });
    });
    builder.addCase(fetchPrices.fulfilled, (state, action) => {
      const id = action.meta.arg;
      pricesAdapter.upsertOne(state, { id, prices: action.payload, requestStatus: REQUEST_STATUS.FULFILLED });
    });
  },
});

const stateSelector = (state: RootState) => state.prices;
const { selectById: pricesByStoreCountrySelector } = pricesAdapter.getSelectors(stateSelector);

const EMPTY_PRICES = {};

const pricesGetter = (state: PricesState) => state?.prices ?? EMPTY_PRICES;
const priceRequestStatusGetter = (state: PricesState) => state?.requestStatus;

export const storeCountrySelector = createSelector(settingsCountrySelector, getStoreCountry);

export const pricesSelector = (state: RootState) =>
  pricesGetter(pricesByStoreCountrySelector(state, storeCountrySelector(state)));

const priceRequestStatusSelector = createSelector(pricesByStoreCountrySelector, priceRequestStatusGetter);

export const isPricesPendingOrFulfilledSelector = createSelector(
  priceRequestStatusSelector,
  (status) => status === REQUEST_STATUS.FULFILLED || status === REQUEST_STATUS.PENDING
);

export default pricesSlice.reducer;
